import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  BsArrowLeft,
  BsFillPersonFill,
  BsKey,
  BsPencilSquare,
} from "react-icons/bs";

import Sidebar from "../../components/sidebar/Sidebar";

import { serviceGetEmployeeInfo } from "../../api/serviceApi";
import { getImageUrl } from "../../utils/helper";

import "./MyProfile.css";

// ============================================================
// HELPERS
// ============================================================

const displayValue = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "—";
  }

  return String(value);
};

const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("en-AU");
};

const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString("en-AU");
};

// ============================================================
// COMPONENT
// ============================================================

const MyProfile = () => {
  const navigate = useNavigate();

  const employeeId =
    sessionStorage.getItem("user_id");

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // ==========================================================
  // LOAD PROFILE
  // ==========================================================

  useEffect(() => {
    if (!employeeId) {
      setInfo(null);
      setLoading(false);

      toast.error(
        "Unable to identify the logged-in user.",
      );

      return;
    }

    let cancelled = false;

    const loadProfile = async () => {
      try {
        setLoading(true);

        const result =
          await serviceGetEmployeeInfo(
            employeeId,
          );

        if (cancelled) {
          return;
        }

        const employee =
          result?.user ||
          result?.employee ||
          result?.data ||
          result;

        setInfo(
          employee &&
            typeof employee === "object"
            ? employee
            : null,
        );
      } catch (error) {
        console.error(
          "Error loading profile:",
          error,
        );

        if (!cancelled) {
          setInfo(null);

          toast.error(
            error?.message ||
              "Failed to load your profile.",
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [employeeId]);

  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {
    return (
      <div className="profile-page">
        <Sidebar />

        <main className="profile-container">
          <div className="profile-state">
            <div className="profile-state-icon">
              <BsFillPersonFill />
            </div>

            <strong>
              Loading profile...
            </strong>

            <span>
              Please wait while your profile information is loaded.
            </span>
          </div>
        </main>
      </div>
    );
  }

  // ==========================================================
  // NOT FOUND
  // ==========================================================

  if (!info) {
    return (
      <div className="profile-page">
        <Sidebar />

        <main className="profile-container">
          <div className="profile-state">
            <div className="profile-state-icon">
              <BsFillPersonFill />
            </div>

            <strong>
              Profile not found
            </strong>

            <span>
              Your employee profile could not be loaded.
            </span>

            <button
              type="button"
              className="profile-btn primary"
              onClick={() => navigate(-1)}
            >
              <BsArrowLeft />
              Back
            </button>
          </div>
        </main>
      </div>
    );
  }

  // ==========================================================
  // NORMALISE DATA
  // ==========================================================

  const employmentType =
    info.employmentType ||
    info.employment_type ||
    "";

  const addedAt =
    info.addedAt ||
    info.added_at ||
    "";

  const updatedAt =
    info.updatedAt ||
    info.updated_at ||
    "";

  const imageUrl =
    getImageUrl(info.imageUrl);

  const employeeName =
    info.name ||
    info.username ||
    "My Profile";

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div className="profile-page">
      <Sidebar />

      <main className="profile-container">
        {/* HEADER */}

        <header className="profile-header">
          <div>
            <button
              type="button"
              className="profile-back"
              onClick={() => navigate(-1)}
            >
              <BsArrowLeft />
              Back
            </button>

            <h1>My Profile</h1>

            <p>
              View your account, employment and personal information.
            </p>
          </div>

          <Link
            // to="/profile/update"
            className="profile-btn primary"
          >
            <BsPencilSquare />
            Edit Profile
          </Link>
        </header>

        {/* MAIN CARD */}

        <div className="profile-card">
          {/* LEFT SIDEBAR */}

          <aside className="profile-sidebar">
            <div className="profile-wrapper">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={employeeName}
                  className="profile-image"
                  onError={(event) => {
                    event.currentTarget.style.display =
                      "none";

                    event.currentTarget
                      .closest(
                        ".profile-wrapper",
                      )
                      ?.classList.add(
                        "image-error",
                      );
                  }}
                />
              ) : (
                <BsFillPersonFill className="profile-icon" />
              )}

              <div className="profile-image-fallback">
                <BsFillPersonFill />
              </div>
            </div>

            <div className="profile-summary">
              <strong>
                {employeeName}
              </strong>

              <span>
                {info.username
                  ? `@${info.username}`
                  : info.email || "Employee"}
              </span>
            </div>

            <div className="profile-role">
              <span>Role</span>

              <strong>
                {displayValue(info.role)}
              </strong>
            </div>

            {employmentType && (
              <div className="profile-sidebar-meta">
                <span>
                  Employment Type
                </span>

                <strong>
                  {employmentType}
                </strong>
              </div>
            )}
          </aside>

          {/* DETAILS */}

          <div className="profile-details">
            {/* ACCOUNT */}

            <section className="profile-section">
              <div className="profile-section-header">
                <h2>
                  Account Information
                </h2>

                <p>
                  Your account and employment details.
                </p>
              </div>

              <div className="profile-grid">
                <ProfileField
                  label="Username"
                  value={info.username}
                />

                <ProfileField
                  label="Email"
                  value={info.email}
                />

                <ProfileField
                  label="Role"
                  value={info.role}
                />

                <ProfileField
                  label="Employment Type"
                  value={employmentType}
                />
              </div>
            </section>

            <div className="profile-divider" />

            {/* PERSONAL */}

            <section className="profile-section">
              <div className="profile-section-header">
                <h2>
                  Personal Details
                </h2>

                <p>
                  Your contact and personal information.
                </p>
              </div>

              <div className="profile-grid">
                <ProfileField
                  label="Name"
                  value={info.name}
                />

                <ProfileField
                  label="Phone"
                  value={info.phone}
                />

                <ProfileField
                  label="Date of Birth"
                  value={
                    info.dob
                      ? formatDate(info.dob)
                      : ""
                  }
                />

                {info.gender && (
                  <ProfileField
                    label="Gender"
                    value={info.gender}
                  />
                )}

                <ProfileField
                  label="Address"
                  value={info.address}
                  full
                />
              </div>
            </section>

            {/* ACTIVITY */}

            {(addedAt || updatedAt) && (
              <>
                <div className="profile-divider" />

                <section className="profile-section">
                  <div className="profile-section-header">
                    <h2>Activity</h2>

                    <p>
                      Account creation and modification information.
                    </p>
                  </div>

                  <div className="profile-grid">
                    {addedAt && (
                      <ProfileField
                        label="Added At"
                        value={formatDateTime(
                          addedAt,
                        )}
                      />
                    )}

                    {updatedAt && (
                      <ProfileField
                        label="Last Updated At"
                        value={formatDateTime(
                          updatedAt,
                        )}
                      />
                    )}
                  </div>
                </section>
              </>
            )}

            {/* ACTIONS */}

            <div className="profile-actions">
              <button
                type="button"
                className="profile-btn ghost"
                onClick={() => navigate(-1)}
              >
                <BsArrowLeft />
                Back
              </button>

              <button
                type="button"
                className="profile-btn ghost"
                // onClick={() =>
                //   navigate("/profile/change")
                // }
              >
                <BsKey />
                Change Password
              </button>

              <Link
                // to="/profile/update"
                className="profile-btn primary"
              >
                <BsPencilSquare />
                Edit Profile
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

// ============================================================
// PROFILE FIELD
// ============================================================

const ProfileField = ({
  label,
  value,
  full = false,
}) => (
  <div
    className={`profile-field ${
      full ? "profile-full" : ""
    }`}
  >
    <span className="profile-label">
      {label}
    </span>

    <span>
      {displayValue(value)}
    </span>
  </div>
);

export default MyProfile;